import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Tooltip, Typography, Checkbox, IconButton } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { useParams } from "react-router-dom";
import AuditComponent from "../../components/AuditComponent";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import { IconCompareReportSvg } from "../../common/utils/icons";
import {
  formattedJson,
  getFormattedDateTime,
  removeCheckboxFromJson,
} from "../../services/utils";
import useFetchComparisonScoringConfigBackup from "../../hooks/useFetchComparisonScoringConfigBackup";
import { getComparisonScoringConfigByRule } from "../../services/scoringService";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";

const AuditComparisonScoring: React.FC = () => {
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const { id } = useParams();
  const ruleId = id ? parseInt(id) : undefined;
  const defaultPage = process.env.REACT_APP_DEFAULT_PAGE;
  const defaultPageSize = process.env.REACT_APP_DEFAULT_PAGE_SIZE;
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1,
  );
  const [pSize, setPSize] = useState<number>(
    defaultPageSize ? parseInt(defaultPageSize) : 25,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [auditSource, setAuditSource] = useState("");
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [selectedInfoData, setSelectedInfoData] = useState<any>(null);
  const [configData, setConfigData] = useState<any>({});
  const configDataRef = useRef<any>({});

  useEffect(() => {
    if (!ruleId || ruleId <= 0) return;
    setIsLoading(true);
    getComparisonScoringConfigByRule(ruleId)
      .then((result) => setConfigData(result || {}))
      .catch(() => setConfigData({}))
      .finally(() => setIsLoading(false));
  }, [ruleId]);

  useEffect(() => {
    configDataRef.current = configData || {};
  }, [configData]);

  useEffect(() => {
    setCurrentBreadcrumbPage({
      name: "Comparison Scoring Config",
      id: ruleId ?? null,
    });
    return () => {
      setCurrentBreadcrumbPage({ name: "", id: null });
    };
  }, [ruleId]);

  const [backupData] = useFetchComparisonScoringConfigBackup({
    ruleId,
    setIsLoading,
    page,
    pSize,
  });

  const toggleSelection = (row: any) => {
    setSelectedRows((prev) => {
      if (prev.some((r) => r.id === row.id))
        return prev.filter((r) => r.id !== row.id);
      if (prev.length < 2) return [...prev, removeCheckboxFromJson(row)];
      return [prev[0], removeCheckboxFromJson(row)];
    });
  };

  const compareCurrentItem = (data: any) => {
    const currentConfig = configDataRef.current;
    if (!currentConfig || Object.keys(currentConfig).length === 0) return;
    const newData = removeCheckboxFromJson(data);
    setSelectedRows([newData, currentConfig]);
    setAuditSource("currentCompare");
    setOpenDialog(true);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        width: 1,
        flex: 0,
        minWidth: 0,
        renderCell: () => null,
      },
      {
        field: "checkbox",
        headerName: "",
        width: 80,
        renderCell: (params) => {
          const isSelected = selectedRows.some((r) => r.id === params.row.id);
          return (
            <Box sx={{ paddingTop: "4px" }}>
              <Checkbox
                checked={isSelected}
                onChange={() => toggleSelection(params.row)}
                sx={{ "&.Mui-checked": { color: "#FFA500" } }}
              />
            </Box>
          );
        },
      },
      { field: "rule_id", headerName: "Rule ID", minWidth: 100, flex: 0.7 },
      {
        field: "created_date",
        headerName: "Last Modified",
        minWidth: 160,
        flex: 1,
        renderCell: (params) => getFormattedDateTime(params.value),
      },
      {
        field: "modified_by_user",
        headerName: "Modified By",
        minWidth: 140,
        flex: 1,
        renderCell: (params: any) => (
          <LimitChractersWithTooltip value={params?.value?.username} />
        ),
      },
      {
        field: "comment",
        headerName: "Comments",
        minWidth: 150,
        flex: 1,
        renderCell: (params) => (
          <LimitChractersWithTooltip value={params.value || ""} />
        ),
      },
      {
        field: "info",
        headerName: "Config Info",
        minWidth: 120,
        flex: 1,
        renderCell: (params) => {
          const { checkbox, ...row } = params.row;
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Tooltip
                componentsProps={{
                  tooltip: { className: "wide-tooltip w-380" },
                }}
                title={
                  <pre
                    style={{
                      whiteSpace: "pre-wrap",
                      margin: 0,
                      maxHeight: "200px",
                      overflowY: "auto",
                    }}
                  >
                    <React.Fragment>
                      <Typography color="inherit">Config Info</Typography>
                      <Typography>
                        {formattedJson(JSON.stringify(row))}
                      </Typography>
                    </React.Fragment>
                  </pre>
                }
              >
                <InfoIcon sx={{ width: "16px", cursor: "pointer" }} />
              </Tooltip>
              <Tooltip title="Expand to view full details" placement="top">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedInfoData(row);
                    setOpenInfoDialog(true);
                  }}
                  sx={{
                    padding: "4px",
                    "&:hover": { backgroundColor: "rgba(255, 165, 0, 0.1)" },
                  }}
                >
                  <OpenInFullIcon sx={{ width: "16px", height: "16px" }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: "action",
        headerName: "Actions",
        align: "center",
        headerAlign: "center",
        width: 130,
        renderCell: (params) => (
          <Tooltip title="Compare with Current" placement="top">
            <IconButton
              className="datagrid-action-btn"
              color="inherit"
              disabled={
                !configDataRef.current ||
                Object.keys(configDataRef.current).length === 0
              }
              onClick={() => compareCurrentItem(params.row)}
            >
              <IconCompareReportSvg />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [selectedRows],
  );

  return (
    <>
      <AuditComponent
        auditColumnData={columns}
        auditRowData={backupData || []}
        isLoading={isLoading}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        toggleSelection={toggleSelection}
        dataListTitle="Comparison Scoring Config History"
        page={page}
        setPage={setPage}
        pSize={pSize}
        setPSize={setPSize}
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        auditSource={auditSource}
        setAuditSource={setAuditSource}
        singlePageMaxHeightDiff={333}
      />
      <ViewPayloadDialog
        openDialog={openInfoDialog}
        dialogTitle="Config Info"
        payload={selectedInfoData}
        handleDialogClose={() => setOpenInfoDialog(false)}
      />
    </>
  );
};

export default AuditComparisonScoring;
